import React, { useCallback, useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { AudioPlayer } from '../components/library';
import { Button, SectionHeader, Spinner, StatusBadge } from '../components/common';
import ReportDetail, {
  REASON_LABELS,
  STATUS_LABELS,
  STATUS_TONES,
  formatReportDate,
  type ReportAction,
} from '../components/admin/ReportDetail';
import ConversionDiagnostics from '../components/admin/ConversionDiagnostics';
import { adminApi } from '../api/admin';
import { ConversionReport, ReportStatus } from '../types';

type Filter = ReportStatus | 'all';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'issue_raised', label: 'Issue raised' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'all', label: 'All' },
];

export function AdminInboxPage() {
  const [filter, setFilter] = useState<Filter>('open');
  const [reports, setReports] = useState<ConversionReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState<ReportAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [listeningId, setListeningId] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const selected = reports.find((r) => r.id === selectedId) ?? null;

  const loadReports = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const response = await adminApi.listReports(filter === 'all' ? undefined : filter);
      setReports(response.items);
      setTotal(response.total);
      setSelectedId((current) =>
        current && response.items.some((r) => r.id === current)
          ? current
          : (response.items[0]?.id ?? null)
      );
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const replaceReport = (updated: ConversionReport) => {
    setReports((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  const runAction = async (
    action: ReportAction,
    fn: () => Promise<ConversionReport | void>
  ) => {
    if (busy) return;
    setBusy(action);
    setActionError(null);
    try {
      const updated = await fn();
      if (updated) replaceReport(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const handleListen = (report: ConversionReport) =>
    runAction('listen', async () => {
      const { audioUrl: url } = await adminApi.getAudioUrl(report.id);
      setAudioUrl(url);
      setListeningId(report.id);
    });

  const handleCreateIssue = (report: ConversionReport) =>
    runAction('issue', () => adminApi.createIssue(report.id));

  const handleRefund = (report: ConversionReport) => {
    const who = report.userEmail || report.userId;
    if (!window.confirm(`Refund 1 credit to ${who}? This cannot be undone.`)) return;
    void runAction('refund', async () => {
      const { report: updated } = await adminApi.refundReport(report.id);
      return updated;
    });
  };

  const handleSaveNotes = (report: ConversionReport, notes: string) =>
    runAction('notes', () => adminApi.updateReport(report.id, { adminNotes: notes }));

  const handleSetStatus = (report: ConversionReport, status: ReportStatus) =>
    runAction('status', () => adminApi.updateReport(report.id, { status }));

  const stopListening = () => {
    setAudioUrl(null);
    setListeningId(null);
  };

  const isPlayerVisible = !!(audioUrl && listeningId);
  const listening = reports.find((r) => r.id === listeningId) ?? null;

  return (
    <Layout>
      <div className={`max-w-6xl mx-auto animate-fade-in ${isPlayerVisible ? 'pb-32' : ''}`}>
        <SectionHeader
          eyebrow="inbox"
          title="Review inbox"
          description="Failed conversions, plus editions users have flagged as poor quality. Inspect, raise an issue, refund."
          className="mb-8"
        />

        <div className="mb-6 flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter reports">
          {FILTERS.map((f) => (
            <Button
              key={f.value}
              role="tab"
              aria-selected={filter === f.value}
              variant={filter === f.value ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
          <span className="ml-auto font-mono text-xs tracking-widest uppercase text-muted-foreground">
            {total} report{total === 1 ? '' : 's'}
          </span>
        </div>

        {listError && (
          <div className="mb-6 p-6 bg-destructive/10 border border-destructive/20 rounded-sm">
            <p className="font-body text-destructive mb-3">{listError}</p>
            <Button variant="secondary" size="sm" onClick={() => void loadReports()}>
              Try again
            </Button>
          </div>
        )}

        {loading && reports.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : reports.length === 0 && !listError ? (
          <div className="text-center py-16 bg-card border border-border rounded-sm">
            <h3 className="font-display text-xl font-medium text-foreground">Inbox zero</h3>
            <p className="mt-2 font-body text-muted-foreground">
              No {filter === 'all' ? '' : STATUS_LABELS[filter].toLowerCase() + ' '}reports right now.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6">
            <ul className="space-y-2" aria-label="Reports">
              {reports.map((report) => {
                const active = report.id === selectedId;
                return (
                  <li key={report.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(report.id);
                        setActionError(null);
                      }}
                      aria-current={active ? 'true' : undefined}
                      className={`w-full text-left bg-card border rounded-sm p-4 transition-colors ${
                        active
                          ? 'border-primary'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <StatusBadge tone={STATUS_TONES[report.status]}>
                          {STATUS_LABELS[report.status]}
                        </StatusBadge>
                        <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">
                          {formatReportDate(report.createdAt)}
                        </span>
                      </div>
                      <p className="font-display text-base font-medium text-foreground truncate">
                        {report.conversionTitle || 'Untitled edition'}
                      </p>
                      <p className="mt-1 font-body text-xs text-muted-foreground truncate">
                        {REASON_LABELS[report.reason] ?? report.reason} ·{' '}
                        {report.userEmail || report.userId}
                      </p>
                      {(report.autoFlagged ||
                        report.refunded ||
                        report.githubIssueNumber != null) && (
                        <p className="mt-2 font-mono text-xs tracking-wide uppercase text-muted-foreground">
                          {[
                            report.autoFlagged && 'auto-flagged',
                            report.refunded && 'refunded',
                            report.githubIssueNumber != null && `issue #${report.githubIssueNumber}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="space-y-6">
              {selected ? (
                <ReportDetail
                  report={selected}
                  busy={busy}
                  error={actionError}
                  isListening={listeningId === selected.id}
                  onListen={() => void handleListen(selected)}
                  onCreateIssue={() => void handleCreateIssue(selected)}
                  onRefund={() => handleRefund(selected)}
                  onSaveNotes={(notes) => void handleSaveNotes(selected, notes)}
                  onSetStatus={(status) => void handleSetStatus(selected, status)}
                />
              ) : (
                <p className="font-body text-sm text-muted-foreground">
                  Select a report to review it.
                </p>
              )}
              {selected && <ConversionDiagnostics key={selected.id} reportId={selected.id} />}
            </div>
          </div>
        )}
      </div>

      {isPlayerVisible && listening && (
        <AudioPlayer
          key={audioUrl}
          src={audioUrl as string}
          title={listening.conversionTitle || 'Untitled edition'}
          subtitle={listening.userEmail || listening.userId}
          onClose={stopListening}
        />
      )}
    </Layout>
  );
}

export default AdminInboxPage;
